import { FC } from 'react';
import { Empty } from 'antd';
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import { FilterAltOutlined } from '@mui/icons-material';
import { SearchBar } from '@plataforma-xvia/design-system-es';

import { Container } from '../../components/container/container';
import { Pagination } from '../../components';
import {
  CheckboxCheckedIcon,
  CheckboxUncheckedIcon,
} from '../../icons/CheckboxIcon';
import { MedicineCard } from '../../components/cards/medicine-card/medicine-card';
import { IMedicine, IMedicinesFilterOptions } from '../../apis/medicines';

import styles from './medicines.module.css';

type FilterKey =
  | 'disponibilidade'
  | 'formaFarmaceutica'
  | 'componenteAgrupado'
  | 'responsabilidade';

export interface MedicinesProps {
  list: IMedicine[];
  isLoading: boolean;
  total: number;
  totalPages: number;
  page: number;

  searchValue: string;
  onChangeSearch: (text: string) => void;

  filterOptions: IMedicinesFilterOptions;

  selectedDisponibilidade: string[];
  selectedFormaFarmaceutica: string[];
  selectedComponenteAgrupado: string[];
  selectedResponsabilidade: string[];
  onToggleFilter: (key: FilterKey, value: string) => void;
  onClearFilterGroup: (key: FilterKey) => void;
  onClearAllFilters: () => void;
  hasActiveFilters: boolean;

  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
}

const FilterGroup: FC<{
  title: string;
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
  onClear: () => void;
}> = ({ title, options, selected, onToggle, onClear }) => {
  if (options.length === 0) return null;

  return (
  <section className={styles.filterGroup}>
    <header className={styles.filterGroupHeader}>
      <h3 className={styles.filterGroupTitle}>{title}</h3>
      {selected.length > 0 && (
        <button
          type="button"
          className={styles.filterGroupClear}
          onClick={onClear}
          aria-label={`Limpar filtros de ${title}`}
        >
          Limpar
        </button>
      )}
    </header>

    <ul className={styles.filterOptions}>
      {options.map((value) => (
        <li key={value}>
          <FormControlLabel
            className={styles.filterOptionLabel}
            control={
              <Checkbox
                checked={selected.includes(value)}
                onChange={() => onToggle(value)}
                size="small"
                icon={<CheckboxUncheckedIcon />}
                checkedIcon={<CheckboxCheckedIcon />}
              />
            }
            label={value}
          />
        </li>
      ))}
    </ul>
  </section>
  );
};

export const Medicines: FC<MedicinesProps> = ({
  list,
  isLoading,
  total,
  totalPages,
  page,
  searchValue,
  onChangeSearch,
  filterOptions,
  // selectedDisponibilidade, // ES-868: filtro de disponibilidade desativado
  selectedFormaFarmaceutica,
  selectedComponenteAgrupado,
  selectedResponsabilidade,
  onToggleFilter,
  onClearFilterGroup,
  onClearAllFilters,
  hasActiveFilters,
  onPageChange,
}) => {
  return (
    <main className={styles.pageContainer}>
      <Container>
        <div className={styles.searchContainer}>
          <SearchBar
            onClickSearch={() => {}}
            onChangeText={onChangeSearch}
            value={searchValue}
            placeholder="Pesquisar medicamento..."
            aria-label="Pesquisar medicamento"
          />
        </div>

        <div
          className={styles.resultsHeader}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className={styles.resultsCount}>
            {isLoading
              ? 'Buscando medicamentos...'
              : `${total} medicamento${total === 1 ? '' : 's'} encontrado${total === 1 ? '' : 's'}`}
          </span>
        </div>

        <div className={styles.layout}>
          <aside
            className={styles.sidebar}
            aria-labelledby="medicines-filters-heading"
          >
            <header className={styles.sidebarHeader}>
              <h2
                id="medicines-filters-heading"
                className={styles.sidebarTitle}
              >
                <FilterAltOutlined fontSize="small" aria-hidden="true" />
                Filtros
              </h2>
              {hasActiveFilters && (
                <button
                  type="button"
                  className={styles.sidebarClearAll}
                  onClick={onClearAllFilters}
                  aria-label="Limpar todos os filtros aplicados"
                >
                  Limpar todos
                </button>
              )}
            </header>

            {/* ES-868: filtro de disponibilidade desativado — manter para reativação futura
            <FilterGroup
              title="Disponibilidade"
              options={filterOptions.disponibilidade}
              selected={selectedDisponibilidade}
              onToggle={(v) => onToggleFilter('disponibilidade', v)}
              onClear={() => onClearFilterGroup('disponibilidade')}
            />
            */}
            <FilterGroup
              title="Forma Farmacêutica"
              options={filterOptions.formaFarmaceutica}
              selected={selectedFormaFarmaceutica}
              onToggle={(v) => onToggleFilter('formaFarmaceutica', v)}
              onClear={() => onClearFilterGroup('formaFarmaceutica')}
            />
            <FilterGroup
              title="Componente"
              options={filterOptions.componenteAgrupado}
              selected={selectedComponenteAgrupado}
              onToggle={(v) => onToggleFilter('componenteAgrupado', v)}
              onClear={() => onClearFilterGroup('componenteAgrupado')}
            />
            <FilterGroup
              title="Responsabilidade de aquisição"
              options={filterOptions.responsabilidade}
              selected={selectedResponsabilidade}
              onToggle={(v) => onToggleFilter('responsabilidade', v)}
              onClear={() => onClearFilterGroup('responsabilidade')}
            />
          </aside>

          <section
            className={styles.main}
            aria-labelledby="medicines-results-heading"
          >
            <h2 id="medicines-results-heading" className={styles.srOnly}>
              Resultados da busca
            </h2>

            {isLoading ? (
              <div className={styles.loadingState}>
                <CircularProgress aria-label="Carregando medicamentos" />
              </div>
            ) : list.length === 0 ? (
              <div className={styles.emptyState}>
                <Empty description="Nenhum medicamento encontrado" />
              </div>
            ) : (
              <ul className={styles.grid} aria-label="Lista de medicamentos">
                {list.map((medicine) => (
                  <li key={medicine.id} className={styles.gridItem}>
                    <MedicineCard
                      title={medicine.denominacaoGenerica}
                      formaFarmaceutica={medicine.formaFarmaceutica}
                      localDeRetirada={medicine.localDeRetirada}
                      indicacao={medicine.indicacaoClinicaPcdt}
                      componenteAgrupado={medicine.componenteAgrupado}
                      responsabilidade={medicine.responsabilidade}
                      disponibilidade={medicine.disponibilidade}
                    />
                  </li>
                ))}
              </ul>
            )}

            {totalPages > 1 && (
              <div className={styles.paginationWrapper}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={onPageChange}
                />
              </div>
            )}
          </section>
        </div>
      </Container>
    </main>
  );
};
